import { useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/pages/home/components/Navbar';
import Footer from '@/pages/home/components/Footer';
import { articleContents, type ArticlePageContent } from '@/mocks/articleContent';

// Map slug from URL to article content key
const slugToContentKey: Record<string, string> = {
  'oopbuy-spreadsheet-guide': 'oopbuy-spreadsheet-guide',
  'beste-sneaker': 'beste-sneaker',
  'qc-fotos-guide': 'qc-fotos-guide',
  'versand-deutschland': 'versand-deutschland',
  'brand-nike': 'brand-nike',
  'brand-adidas': 'brand-adidas',
  'yupoo-guide': 'yupoo-guide',
  'taobao-guide': 'taobao-guide',
  'beste-hoodies': 'beste-hoodies',
  'beste-jacken': 'beste-jacken',
  'sicher-einkaufen': 'sicher-einkaufen',
  '1688-guide': '1688-guide',
  'designer-bags': 'designer-bags',
};

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();

  const contentKey = slug ? slugToContentKey[slug] : undefined;
  const content: ArticlePageContent | undefined = contentKey ? articleContents[contentKey] : undefined;

  const schema = useMemo(() => {
    if (!content) return null;
    return {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: content.metaTitle,
      description: content.metaDescription,
      image: content.heroImage,
      author: { '@type': 'Person', name: content.author },
      datePublished: content.datePublished,
      dateModified: content.dateModified,
      publisher: { '@type': 'Organization', name: 'OOPBuy Spreadsheet' },
      inLanguage: 'de',
      mainEntityOfPage: {
        '@type': 'WebPage',
        '@id': `https://oopbuyspread.fit/artikel/${slug}`,
      },
    };
  }, [content, slug]);

  if (!content) {
    return (
      <>
        <Navbar />
        <main className="pt-16 min-h-screen flex items-center justify-center bg-background-50">
          <div className="text-center px-6">
            <h1 className="font-heading text-3xl font-bold text-foreground-950 mb-4">Artikel nicht gefunden</h1>
            <p className="text-foreground-600 mb-6">Der gesuchte Artikel existiert leider nicht.</p>
            <Link to="/" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-foreground-900 text-background-50 text-sm font-medium hover:bg-foreground-800 transition-colors whitespace-nowrap cursor-pointer">
              <i className="ri-arrow-left-line"></i>
              Zurück zur Startseite
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      {schema && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
        />
      )}
      <Navbar />
      <main className="pt-16">
        {/* Article Header */}
        <section className="py-12 md:py-20 px-6 md:px-10 bg-background-50">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col lg:flex-row gap-10 lg:gap-16 items-start">
              <div className="lg:w-[55%]">
                <nav className="flex flex-wrap items-center gap-2 text-xs text-foreground-500 mb-6" aria-label="Breadcrumb">
                  <Link to="/" className="hover:text-foreground-800 transition-colors">Startseite</Link>
                  <i className="ri-arrow-right-s-line"></i>
                  <Link to="/" className="hover:text-foreground-800 transition-colors">Ratgeber</Link>
                  <i className="ri-arrow-right-s-line"></i>
                  <span className="text-foreground-900 font-medium">{content.title}</span>
                </nav>

                <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-950 leading-tight mb-4">
                  {content.title}
                </h1>

                <p className="text-foreground-500 text-sm mb-2 italic">{content.subtitle}</p>

                <div className="flex flex-wrap items-center gap-4 text-xs text-foreground-500 mb-6">
                  <span className="flex items-center gap-1">
                    <i className="ri-user-line"></i> {content.author}
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-calendar-line"></i> Aktualisiert: {new Date(content.dateModified).toLocaleDateString('de-DE')}
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-time-line"></i> Lesezeit: {content.readingTime}
                  </span>
                </div>

                <p className="text-foreground-600 text-base leading-relaxed mb-8">
                  {content.introText}
                </p>

                <Link
                  to="/"
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-foreground-900 text-background-50 text-sm font-medium hover:bg-foreground-800 transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-arrow-left-line"></i>
                  Zurück zur Startseite
                </Link>
              </div>

              <div className="lg:w-[45%] w-full">
                <div className="rounded-2xl overflow-hidden">
                  <img
                    src={content.heroImage}
                    alt={`OOPBuy Spreadsheet ${content.title}`}
                    title={`OOPBuy Spreadsheet ${content.title}`}
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Article Body */}
        <section className="py-12 md:py-16 px-6 md:px-10 bg-background-50">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col lg:flex-row gap-10 lg:gap-16">
              <article className="lg:w-[65%] max-w-none">
                <div className="space-y-10">
                  {content.sections.map((section, idx) => (
                    <div key={idx}>
                      <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-4">
                        {section.heading}
                      </h2>
                      {section.paragraphs.map((para, pIdx) => (
                        <p key={pIdx} className="text-foreground-700 text-sm md:text-base leading-relaxed mb-4">
                          {para.includes('OOPBuy Spreadsheet') ? (
                            <>
                              {para.split('OOPBuy Spreadsheet').reduce((acc: React.ReactNode[], part, i, _arr) => {
                                if (i === 0) {
                                  if (part) acc.push(part);
                                } else {
                                  acc.push(<strong key={`strong-${idx}-${pIdx}-${i}`}>OOPBuy Spreadsheet</strong>);
                                  if (part) acc.push(part);
                                }
                                return acc;
                              }, [])}
                            </>
                          ) : para.includes('OOPBuy Spreadsheet Deutschland') ? (
                            <>
                              {para.split('OOPBuy Spreadsheet Deutschland').reduce((acc: React.ReactNode[], part, i) => {
                                if (i === 0) {
                                  if (part) acc.push(part);
                                } else {
                                  acc.push(
                                    <Link key={`link-${idx}-${pIdx}-${i}`} to="/" className="text-accent-600 hover:text-accent-700 font-medium">
                                      OOPBuy Spreadsheet Deutschland
                                    </Link>
                                  );
                                  if (part) acc.push(part);
                                }
                                return acc;
                              }, [])}
                            </>
                          ) : (
                            para
                          )}
                        </p>
                      ))}
                      {section.listItems && (
                        <ul className={`space-y-2 text-sm text-foreground-700 mb-4 ${section.isOrdered ? 'list-decimal list-inside' : ''}`}>
                          {section.listItems.map((item, lIdx) => (
                            <li key={lIdx} className="flex items-start gap-2">
                              {!section.isOrdered && <i className="ri-check-line text-accent-500 mt-0.5 shrink-0"></i>}
                              <span>{item}</span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  ))}

                  <div>
                    <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-4">
                      Fazit
                    </h2>
                    <p className="text-foreground-700 text-sm md:text-base leading-relaxed mb-6">
                      {content.conclusion}
                    </p>
                  </div>

                  <div className="pt-6 border-t border-background-200/60">
                    <Link
                      to="/"
                      className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-foreground-900 text-background-50 text-sm font-medium hover:bg-foreground-800 transition-colors whitespace-nowrap cursor-pointer"
                    >
                      <i className="ri-arrow-left-line"></i>
                      Zurück zur Startseite
                    </Link>
                    <span className="ml-4 text-sm text-foreground-500">
                      Erfahre mehr auf unserer <Link to="/" className="text-accent-600 hover:text-accent-700 font-medium">Hauptseite</Link>.
                    </span>
                  </div>
                </div>
              </article>

              {/* Sidebar */}
              <aside className="lg:w-[35%]">
                <div className="sticky top-24 space-y-6">
                  <div className="rounded-2xl bg-background-100 border border-background-200/60 p-5">
                    <h3 className="font-heading text-lg font-bold text-foreground-950 mb-4">OOPBuy Spreadsheet</h3>
                    <p className="text-sm text-foreground-600 mb-4">
                      Starte jetzt und entdecke tausende Produkte.
                    </p>
                    <a
                      href="https://kaspreadsheet.com"
                      target="_blank"
                      rel="noopener nofollow"
                      className="inline-flex items-center justify-center gap-2 w-full px-4 py-2.5 rounded-lg bg-foreground-900 text-background-50 text-sm font-medium hover:bg-foreground-800 transition-colors whitespace-nowrap cursor-pointer"
                    >
                      Jetzt öffnen
                      <i className="ri-arrow-right-up-line text-xs"></i>
                    </a>
                  </div>

                  <div className="rounded-2xl overflow-hidden border border-background-200/60">
                    <div className="relative aspect-[4/3] overflow-hidden bg-background-100">
                      <img
                        src="https://readdy.ai/api/search-image?query=Minimal%20product%20display%20of%20fashion%20accessories%20including%20sneakers%20bags%20and%20folded%20garments%20on%20warm%20beige%20surface%20soft%20natural%20lighting%20clean%20editorial%20style%20professional%20catalog%20aesthetic%20modern%20European%20design&width=600&height=450&seq=sidebar-category-v2&orientation=landscape"
                        alt="OOPBuy Spreadsheet Kategorien"
                        title="OOPBuy Spreadsheet Kategorien Übersicht"
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="p-4 bg-background-50">
                      <h4 className="text-sm font-semibold text-foreground-900 mb-1">Beliebte Kategorien</h4>
                      <p className="text-xs text-foreground-500 mb-3">Entdecke alle Kategorien auf der Hauptseite</p>
                      <Link
                        to="/"
                        className="text-xs text-accent-600 hover:text-accent-700 font-medium transition-colors"
                      >
                        Zur Startseite →
                      </Link>
                    </div>
                  </div>
                </div>
              </aside>
            </div>
          </div>
        </section>

        {/* Related Articles */}
        <section className="py-12 md:py-16 px-6 md:px-10 bg-background-100">
          <div className="max-w-7xl mx-auto">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-8">
              Das könnte dich interessieren
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {[
                { title: 'Die besten Sneaker im OOPBuy Spreadsheet', slug: 'beste-sneaker', img: 'https://readdy.ai/api/search-image?query=Trendy%20designer%20sneakers%20displayed%20artistically%20on%20warm%20beige%20minimal%20surface%20soft%20natural%20daylight%20editorial%20fashion%20photography%20clean%20composition%20blog%20cover%20style%20elevated%20product%20aesthetic%20with%20gentle%20shadows&width=600&height=750&seq=related-sneaker-v2&orientation=portrait' },
                { title: 'QC Fotos richtig lesen – Qualitätscheck', slug: 'qc-fotos-guide', img: 'https://readdy.ai/api/search-image?query=Professional%20quality%20inspection%20of%20fashion%20garment%20with%20magnifying%20tool%20on%20warm%20beige%20desk%20soft%20natural%20lighting%20editorial%20photography%20detailed%20fabric%20texture%20visible%20clean%20aesthetic%20composition&width=600&height=750&seq=related-qc-v2&orientation=portrait' },
                { title: 'Versand nach Deutschland – Kosten & Zoll', slug: 'versand-deutschland', img: 'https://readdy.ai/api/search-image?query=Elegant%20cardboard%20shipping%20box%20with%20German%20destination%20label%20on%20warm%20beige%20surface%20soft%20natural%20daylight%20editorial%20photography%20clean%20composition%20logistics%20aesthetic%20professional%20style&width=600&height=750&seq=related-shipping-v2&orientation=portrait' },
              ].map((item) => (
                <div key={item.slug} className="group rounded-2xl overflow-hidden border border-background-200/60 hover:border-background-300 transition-all duration-300 bg-background-50">
                  <Link to={`/artikel/${item.slug}`} className="block cursor-pointer">
                    <div className="relative aspect-[3/4] overflow-hidden bg-background-100">
                      <img
                        src={item.img}
                        alt={item.title}
                        title={item.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/50 via-transparent to-transparent"></div>
                      <div className="absolute bottom-0 left-0 right-0 p-4">
                        <h3 className="text-white text-sm font-semibold leading-snug">{item.title}</h3>
                        <Link to="/" className="text-white/70 text-xs hover:text-white transition-colors mt-1 inline-block">Zur Startseite</Link>
                      </div>
                    </div>
                  </Link>
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}